import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Calendar, MessageCircle, Phone, Loader2, Users, Youtube, Mail, MapPin,
  ChevronDown, ChevronUp, Search, Download,
} from 'lucide-react';
import AdminShell from '../components/AdminShell';
import SEO from '../components/SEO';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatHour = (h) => {
  if (h == null) return '—';
  const suffix = h >= 12 ? 'PM' : 'AM';
  const d = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${d}:00 ${suffix}`;
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

const cleanPhone = (p) => (p || '').replace(/\D/g, '');

const statusColor = {
  pending: { bg: '#fef3c7', fg: '#92400e' },
  confirmed: { bg: '#dcfce7', fg: '#166534' },
  completed: { bg: '#dbeafe', fg: '#1e40af' },
  cancelled: { bg: '#fee2e2', fg: '#991b1b' },
};

const AdminBookings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ bookings: [], count: 0 });
  const [service, setService] = useState('all');
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState(null);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) { navigate('/admin/login'); return; }
      const res = await axios.get(`${API}/admin/bookings`, {
        params: { service, status, limit: 200 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      } else {
        toast.error('Failed to load bookings');
      }
    } finally {
      setLoading(false);
    }
  }, [service, status, navigate]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const filtered = data.bookings.filter((b) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (b.name || '').toLowerCase().includes(s) ||
           (b.phone || '').toLowerCase().includes(s) ||
           (b.city || '').toLowerCase().includes(s) ||
           (b.email || '').toLowerCase().includes(s);
  });

  const openWhatsApp = (b) => {
    const phone = cleanPhone(b.phone);
    if (phone.length < 10) { toast.error('Invalid phone number'); return; }
    const whenText = b.slot_date ? `${formatDate(b.slot_date)} at ${formatHour(b.slot_hour)}` : 'your scheduled time';
    const msg = encodeURIComponent(
      `Hi ${b.name}, this is Elderra (+91 89204 55730). Just confirming your ${b.service_label} on ${whenText}. See you soon!`
    );
    const to = phone.length === 10 ? `91${phone}` : phone;
    window.open(`https://wa.me/${to}?text=${msg}`, '_blank', 'noopener');
  };

  const downloadCSV = () => {
    if (filtered.length === 0) {
      toast.error('No bookings to export');
      return;
    }
    const headers = ['Service', 'Name', 'Phone', 'Email', 'Slot Date', 'Slot Time', 'City', 'Address', 'Detail', 'Status', 'Free Slot', 'Notes', 'Created At'];
    const esc = (v) => {
      if (v == null) return '';
      const s = String(v);
      if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const rows = filtered.map((b) => [
      b.service_label,
      b.name,
      b.phone,
      b.email || '',
      b.slot_date || '',
      b.slot_hour != null ? formatHour(b.slot_hour) : '',
      b.city || '',
      b.address || '',
      b.detail || '',
      b.status,
      b.is_free_slot ? 'Yes' : '',
      b.notes || '',
      b.created_at || '',
    ].map(esc).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    // Prepend BOM for Excel UTF-8 compatibility
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const today = new Date().toISOString().split('T')[0];
    const servicePart = service === 'all' ? 'all' : service;
    const filename = `elderra-bookings-${servicePart}-${today}.csv`;
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} booking${filtered.length === 1 ? '' : 's'}`);
  };

  return (
    <AdminShell>
      <SEO title="All Bookings - Elderra Admin" description="All DigiMitra bookings and YouTuber signups." />

      <div data-testid="admin-bookings-page" style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 13, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Bookings</div>
        <h1 style={{ margin: '4px 0 6px', fontSize: 32, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>All Bookings</h1>
        <p style={{ margin: 0, color: '#6b7280', fontSize: 14 }}>Every DigiMitra visit and YouTuber signup — with date, time, address, and one-tap WhatsApp.</p>
      </div>

      {/* WhatsApp tip */}
      <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, padding: '10px 14px', fontSize: 12, color: '#065f46', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <MessageCircle size={16} style={{ color: '#16a34a' }} />
        <span><strong>Tip:</strong> The WhatsApp button opens a chat from whichever account you're logged into on your device. Sign into WhatsApp Business as <strong>+91 89204 55730</strong> to send from the official Elderra number.</span>
      </div>

      {/* Filters */}
      <div data-testid="bookings-filters" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20, background: '#fff', padding: '14px 16px', borderRadius: 12, border: '1px solid #e5e7eb' }}>
        <div style={{ display: 'inline-flex', background: '#f3f4f6', borderRadius: 9, padding: 3 }}>
          {[
            { id: 'all', label: 'All' },
            { id: 'digimitra', label: 'DigiMitra' },
            { id: 'youtuber', label: 'YouTuber' },
          ].map((s) => (
            <button
              key={s.id}
              onClick={() => setService(s.id)}
              data-testid={`filter-service-${s.id}`}
              style={{
                padding: '7px 14px', border: 'none', cursor: 'pointer',
                background: service === s.id ? '#0f172a' : 'transparent',
                color: service === s.id ? '#fff' : '#6b7280',
                borderRadius: 7, fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
              }}
            >
              {s.label}
            </button>
          ))}
        </div>

        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="filter-status"
          style={{ padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontWeight: 600, background: '#fff', fontFamily: 'inherit' }}
        >
          <option value="all">All statuses</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <div style={{ flex: 1, minWidth: 200, position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
          <input
            type="text"
            placeholder="Search by name, phone, city, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="bookings-search"
            style={{ width: '100%', padding: '8px 10px 8px 30px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}
          />
        </div>

        <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
          <span data-testid="bookings-total">{filtered.length}</span> of {data.count} bookings
        </div>

        <button
          data-testid="bookings-export-csv"
          onClick={downloadCSV}
          disabled={loading || filtered.length === 0}
          title="Download currently filtered bookings as CSV"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 9,
            background: filtered.length === 0 ? '#f3f4f6' : '#0f172a',
            color: filtered.length === 0 ? '#9ca3af' : '#fff',
            border: 'none', cursor: filtered.length === 0 ? 'not-allowed' : 'pointer',
            fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => { if (filtered.length > 0) e.currentTarget.style.background = '#000'; }}
          onMouseLeave={(e) => { if (filtered.length > 0) e.currentTarget.style.background = '#0f172a'; }}
        >
          <Download size={14} /> Download CSV
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#9ca3af' }}>
          <Loader2 size={20} style={{ animation: 'adm-spin 1s linear infinite' }} /> Loading bookings...
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="bookings-empty" style={{ padding: 60, textAlign: 'center', background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb' }}>
          <Calendar size={32} style={{ color: '#d1d5db', margin: '0 auto 8px', display: 'block' }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: '#6b7280' }}>No bookings match these filters.</div>
        </div>
      ) : (
        <div data-testid="bookings-list" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map((b) => {
            const isYt = b.service === 'youtuber';
            const ServiceIcon = isYt ? Youtube : Users;
            const tint = isYt ? '#faf5ff' : '#fff0f5';
            const fg = isYt ? '#7c3aed' : '#e5266a';
            const sc = statusColor[b.status] || { bg: '#f3f4f6', fg: '#6b7280' };
            const expanded = expandedId === b.id;

            return (
              <div
                key={b.id}
                data-testid={`booking-row-${b.id}`}
                style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, transition: 'all 0.15s' }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 12, alignItems: 'center' }}>
                  {/* Service icon */}
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: tint, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <ServiceIcon size={18} />
                  </div>

                  {/* Main info */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{b.name}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', background: tint, color: fg, padding: '2px 7px', borderRadius: 999 }}>{b.service_label}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', background: sc.bg, color: sc.fg, padding: '2px 7px', borderRadius: 999 }}>{b.status}</span>
                      {isYt && b.is_free_slot && <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', background: '#fef3c7', color: '#92400e', padding: '2px 7px', borderRadius: 999 }}>FREE</span>}
                    </div>
                    <div style={{ marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12.5, color: '#4b5563' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 600 }}>
                        <Calendar size={12} /> {formatDate(b.slot_date)} {b.slot_hour != null && `· ${formatHour(b.slot_hour)}`}
                      </span>
                      {b.phone && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Phone size={12} /> {b.phone}</span>}
                      {b.city && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><MapPin size={12} /> {b.city}</span>}
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button
                      data-testid={`booking-whatsapp-${b.id}`}
                      onClick={() => openWhatsApp(b)}
                      title="Send WhatsApp from your business account"
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 8, background: '#25D366', color: '#fff', border: 'none', cursor: 'pointer', transition: 'transform 0.15s' }}
                      onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                    >
                      <MessageCircle size={15} />
                    </button>
                    <button
                      data-testid={`booking-expand-${b.id}`}
                      onClick={() => setExpandedId(expanded ? null : b.id)}
                      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 8, background: '#f3f4f6', color: '#374151', border: 'none', cursor: 'pointer' }}
                    >
                      {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </button>
                  </div>
                </div>

                {/* Expanded details */}
                {expanded && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #f3f4f6', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, fontSize: 13 }}>
                    {b.address && (
                      <div><div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Address</div><div style={{ color: '#374151' }}>{b.address}</div></div>
                    )}
                    {b.email && (
                      <div><div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Email</div><a href={`mailto:${b.email}`} style={{ color: '#2563eb', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}><Mail size={12} /> {b.email}</a></div>
                    )}
                    {b.detail && (
                      <div><div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{isYt ? 'Interest' : 'Service'}</div><div style={{ color: '#374151' }}>{b.detail}</div></div>
                    )}
                    {b.notes && (
                      <div style={{ gridColumn: '1 / -1' }}><div style={{ fontSize: 10, color: '#9ca3af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>Notes</div><div style={{ color: '#374151' }}>{b.notes}</div></div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <style>{`@keyframes adm-spin { to { transform: rotate(360deg); } }`}</style>
    </AdminShell>
  );
};

export default AdminBookings;
